/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  differenceInDays,
  endOfDay,
  format,
  formatISO,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  startOfDay,
  subDays,
} from 'date-fns';
import moment from 'moment';
import Report from '../../../../componentsV2/Layout/Report';
import formatNumberAsCurrency from '../../../../utils/formatNumberAsCurrency';
import formatAbbreviatedCurrency from '../../../../utils/formatAbbreviatedCurrency';
import './styles.scss';
import SalesOppExpandRow from './components/SalesExpandRow';
import useSearchParams from '../../../../hooks/useSearchParams';
import { addLoadingList, removeLoadingList, setLoadingDescription } from '../../../../reducers/notification';
import { useReport } from '../../../../context/ReportContext';
import { convertDaysToMonths } from '../../../../utils/convertDaysToMonths';

const SalesOpp = ({ auth, ...props }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [dateRange, setDateRange] = useState([]);
  const [filters, setFilters] = useState({
    opportunityType: [],
    store: [],
    associate: [],
    stage: [],
  });

  const { setValue: setSearchParams, getAll } = useSearchParams();
  const params = getAll();

  const dateField = useMemo(() => {
    return params?.dateField?.[0] || 'expectedClose';
  }, [params?.dateField]);

  const dateFieldOptions = useMemo(
    () => [
      { label: 'Expected Close', value: 'expectedClose' },
      { label: 'Created Date', value: 'createdOn' },
    ],
    [],
  );

  const handleDateFieldChange = useCallback(value => {
    setSearchParams('dateField', value);
  }, []);

  const currentTypeDateOptions = useMemo(() => {
    return dateField === 'createdOn' ? 'past-options' : 'future-options';
  }, [dateField]);

  const { salesOpp, isLoadingSalesOpp } = useReport();
  const { opportunityTypes, reportData, overdueDate } = useMemo(() => {
    return {
      opportunityTypes: salesOpp?.opportunityTypes || [],
      reportData: salesOpp?.reportData || [],
      overdueDate: salesOpp?.overdueDate || new Date(),
    };
  }, [salesOpp]);

  useEffect(() => {
    if (isLoadingSalesOpp) {
      setLoadingDescription("Sit tight! We're compiling your report.");
      addLoadingList('salesOppReport');
    } else {
      removeLoadingList('salesOppReport');
      setLoadingDescription(null);
    }
  }, [isLoadingSalesOpp]);

  useEffect(() => {
    const paramsFilters = {
      opportunityType: params?.opportunityType ? params?.opportunityType : [],
      store: params?.store ? params?.store : [],
      associate: params?.associate ? params?.associate : [],
      stage: params?.stage ? params?.stage : [],
    };
    setFilters(paramsFilters);
    if (params?.startDate && params?.endDate) {
      setDateRange([new Date(params.startDate), new Date(params.endDate)]);
    } else {
      setDateRange([startOfDay(subDays(new Date(), 90)), endOfDay(new Date())]);
    }
  }, [window.location.search]);

  const handleOnDateRangeChange = useCallback(dates => {
    setSearchParams('startDate', formatISO(dates[0]));
    setSearchParams('endDate', formatISO(dates[1]));
  }, []);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const options = useMemo(
    () => [
      {
        title: 'Opportunity Type',
        key: '0-0',
        value: 'opportunityType',
        children: opportunityTypes.map(item => ({
          key: item.id.toString(),
          value: item.id.toString(),
          title: item.name,
        })),
      },
      {
        title: 'Stage',
        key: '0-1',
        value: 'stage',
        children: [
          { key: 'open', value: 'open', title: 'Open' },
          { key: 'won', value: 'won', title: 'Won' },
          { key: 'lost', value: 'lost', title: 'Lost' },
        ],
      },
    ],
    [opportunityTypes],
  );

  const getDefaultPeriod = useMemo(() => {
    if (params?.startDate && params?.endDate) {
      if (differenceInDays(new Date(params.endDate), new Date(params.startDate)) === 90) {
        return dateField === 'createdOn' ? '90days' : 'next-90-days';
      }
      return `${format(new Date(params.startDate), 'MM/dd/yyyy')} - ${format(new Date(params.endDate), 'MM/dd/yyyy')}`;
    }
    return dateField === 'createdOn' ? 'previousmonth' : 'next-90-days';
  }, [dateField]);

  const tableData = useMemo(() => {
    const allStores = auth.stores.filter(store => store.id !== 0).map(item => item.id.toString());

    // Helper function to parse date strings as local dates without timezone conversion
    const parseLocalDate = dateStr => {
      if (!dateStr) return '';
      // Extract date parts and create local date
      const [year, month, day] = dateStr.split('T')[0].split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };

    return reportData
      .map(item => {
        const key = item.salesOppId;
        const createdOn = parseLocalDate(item.salesOppCreatedDate);
        const completeDate = parseLocalDate(item.salesOppCompleteDate);

        let createdBy = item.associateFirstName || '';

        if (createdBy && item.associateLastName) {
          createdBy += ` ${item.associateLastName}`;
        }

        const client = item.clientName || '';
        const store = item.storeName || '';
        const opportunityType = item.opportunityType || '';
        const expectedClose = parseLocalDate(item.salesOppCloseDate);
        const amount = Number(item.salesOppAmount) || 0;
        const opportunityId = item.opportunityId ? `${item.opportunityId}` : '';
        const storeId = item.storeId ? `${item.storeId}` : '';
        const associateId = item.associateId ? `${item.associateId}` : '';
        const clientId = item.clientId ? `${item.clientId}` : '';

        return {
          key,
          createdOn,
          completeDate,
          createdBy,
          client,
          store,
          opportunityType,
          expectedClose,
          amount,
          opportunityId,
          storeId,
          associateId,
          stageWeight: item.stageWeight,
          clientId,
          salesOppId: item.salesOppId,
          stageName: item.stageName,
        };
      })
      .filter(item => {
        if (filters.opportunityType.length && !filters.opportunityType.includes(item.opportunityId)) {
          return false;
        }

        if (filters.stage?.length) {
          const stage = item.stageWeight === 0 ? 'lost' : item.stageWeight === 1 ? 'won' : 'open';
          if (!filters.stage.includes(stage)) {
            return false;
          }
        }

        if (
          filters.store.length === 0
            ? !allStores.includes(item?.storeId?.toString())
            : !filters.store.includes(item?.storeId?.toString())
        ) {
          return false;
        }

        if (filters.associate.length && !filters.associate.includes(item.associateId)) {
          return false;
        }

        const dateValue = dateField === 'createdOn' ? item.createdOn : item.expectedClose;
        if (dateValue && dateRange[0] && dateRange[1]) {
          const start = startOfDay(dateRange[0]);
          const end = endOfDay(dateRange[1]);

          if (!isWithinInterval(dateValue, { start, end })) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (a.createdOn && b.createdOn) {
          return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime();
        }
        if (a.createdOn) {
          return -1;
        }
        if (b.createdOn) {
          return 1;
        }
        return 0;
      });
  }, [reportData, filters, dateRange, dateField]);

  const salesOppOverdue = useMemo(() => {
    return tableData.reduce((acc, curr) => {
      if (curr.expectedClose && curr.expectedClose < new Date()) {
        return acc + 1;
      }

      return acc;
    }, 0);
  }, [tableData]);

  const salesOppProjectedRevenue = useMemo(() => {
    return reportData
      .filter(item => {
        const filterAssociate =
          filters.associate.length === 0 || filters.associate.includes(item.associateId.toString());
        const filterStore = filters.store.length === 0 || filters.store.includes(item.storeId.toString());
        const filterOpportunityType =
          filters.opportunityType.length === 0 || filters.opportunityType.includes(item.opportunityId.toString());
        const rawDateValue = dateField === 'createdOn' ? item.salesOppCreatedDate : item.salesOppCloseDate;
        const filterTimeSpan = moment(rawDateValue).isBetween(moment(dateRange[0]), moment(dateRange[1]));
        return filterAssociate && filterStore && filterOpportunityType && filterTimeSpan;
      })
      .reduce((acc, curr) => {
        if (curr.stageWeight !== 0 && curr.stageWeight !== 1) {
          return acc + parseFloat(curr.salesOppAmount);
        }

        return acc;
      }, 0);
  }, [reportData, filters, dateRange, dateField]);

  const salesOppAmountsByStage = useMemo(() => {
    const amounts = { openAmount: 0, wonAmount: 0, lostAmount: 0 };

    for (let i = 0; i < reportData.length; i++) {
      const item = reportData[i];
      const filterAssociate = filters.associate.length === 0 || filters.associate.includes(item.associateId.toString());
      const filterStore = filters.store.length === 0 || filters.store.includes(item.storeId.toString());
      const filterOpportunityType =
        filters.opportunityType.length === 0 || filters.opportunityType.includes(item.opportunityId.toString());
      const rawDateValue = dateField === 'createdOn' ? item.salesOppCreatedDate : item.salesOppCloseDate;
      const filterTimeSpan = moment(rawDateValue).isBetween(moment(dateRange[0]), moment(dateRange[1]));

      if (filterAssociate && filterStore && filterOpportunityType && filterTimeSpan) {
        const amount = parseFloat(item.salesOppAmount);
        if (item.stageWeight === 1) {
          amounts.wonAmount += amount;
        } else if (item.stageWeight === 0) {
          amounts.lostAmount += amount;
        } else {
          amounts.openAmount += amount;
        }
      }
    }

    return amounts;
  }, [reportData, filters, dateRange, dateField]);

  const openOpportunityTypes = useMemo(() => {
    return opportunityTypes.filter(opp => {
      const findOpp = tableData.find(item => parseInt(item.opportunityId) === opp.id);
      return findOpp;
    });
  }, [opportunityTypes, tableData]);

  const stats = useMemo(() => {
    const days = differenceInDays(new Date(params.endDate), new Date(params.startDate));
    let label = '';
    if (isAfter(new Date(params.endDate), new Date()) && isSameDay(new Date(params.startDate), new Date())) {
      label = ` next ${convertDaysToMonths(days)}`;
    } else if (isBefore(new Date(params.endDate), new Date()) && isSameDay(new Date(params.startDate), new Date())) {
      label = ` last ${convertDaysToMonths(days)}`;
    } else {
      label = ` during ${convertDaysToMonths(days)}`;
    }
    return [
      {
        title: 'Sales Opportunities',
        description: `Across ${openOpportunityTypes.length} types, ${salesOppOverdue} overdue`,
        value: tableData.length,
      },
      {
        title: 'Total Pipeline',
        value: formatNumberAsCurrency(salesOppProjectedRevenue),
        description: `${formatAbbreviatedCurrency(salesOppAmountsByStage.openAmount)} Open, ${formatAbbreviatedCurrency(
          salesOppAmountsByStage.wonAmount,
        )} Closed Won, ${formatAbbreviatedCurrency(salesOppAmountsByStage.lostAmount)} Closed Lost`,
      },
    ];
  }, [salesOppOverdue, salesOppProjectedRevenue, salesOppAmountsByStage, openOpportunityTypes, tableData]);

  const total = useMemo(() => tableData.length, [tableData]);

  const handleFilterChange = useCallback(value => {
    const updatedFilters = {
      opportunityType: [],
      store: [],
      associate: [],
      stage: [],
    };

    value.forEach(item => {
      updatedFilters[item[0]] = [...updatedFilters[item[0]], item[1]];
    });
    Object.keys(updatedFilters).forEach(key => {
      setSearchParams(key, updatedFilters[key].join(','));
    });
  }, []);

  const removeFilter = useCallback(
    (key, value) => {
      const updatedFilters = { ...filters };

      updatedFilters[key] = updatedFilters[key].filter(filter => filter !== value);

      Object.keys(updatedFilters).forEach(key => {
        setSearchParams(key, updatedFilters[key].join(','));
      });
    },
    [filters],
  );

  const graphData = useMemo(() => {
    const data = opportunityTypes.reduce((acc, curr) => {
      acc[curr.name] = [];

      return acc;
    }, {});

    tableData.forEach(item => {
      const dateValue = dateField === 'createdOn' ? item.createdOn : item.expectedClose;
      data[item.opportunityType].push({
        date: dateValue,
        total: 1,
      });
    });

    Object.keys(data).forEach(key => {
      if (!data[key].length) {
        delete data[key];
      }
    });

    return data;
  }, [tableData, opportunityTypes, dateField]);

  const expandedRowRender = useCallback(
    record => {
      return <SalesOppExpandRow merchantId={auth.merchantId} {...record} licenseKey={auth.licenseKey} />;
    },
    [expandedRowKeys],
  );

  const handleExpandRow = useCallback(
    (expand, record) => {
      if (expand) {
        setExpandedRowKeys([...expandedRowKeys, record.key]);
      } else {
        setExpandedRowKeys([...expandedRowKeys].filter(key => key !== record.key));
      }
    },
    [expandedRowKeys],
  );

  const columns = useMemo(() => {
    const storeColumn =
      auth.stores.filter(st => st.id !== 0).length > 1
        ? [
            {
              key: 'opportunityType',
              dataIndex: 'opportunityType',
              title: 'Type',
              sorter: (a, b) => a.opportunityType.localeCompare(b.opportunityType),
            },
          ]
        : [];
    return [
      {
        key: 'createdOn',
        dataIndex: 'createdOn',
        title: 'Created Date',
        sorter: (a, b) => {
          if (a.createdOn && b.createdOn) {
            return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime();
          }
          if (a.createdOn) {
            return -1;
          }
          if (b.createdOn) {
            return 1;
          }
          return 0;
        },
        render: item => (item ? format(item, 'MMM dd, yyyy') : ''),
      },
      {
        key: 'createdBy',
        dataIndex: 'createdBy',
        title: 'Owner',
        sorter: (a, b) => a.createdBy.localeCompare(b.createdBy),
      },
      {
        key: 'client',
        dataIndex: 'client',
        title: 'Client',
        sorter: (a, b) => a.client.localeCompare(b.client),
        render: (_, row) => <Link to={`/Clients?client=${row.clientId}`}>{row.client}</Link>,
      },
      {
        key: 'store',
        dataIndex: 'store',
        title: 'Store',
        sorter: (a, b) => a.store.localeCompare(b.store),
      },
      ...storeColumn,
      {
        key: 'stageName',
        dataIndex: 'stageName',
        title: 'Stage',
        sorter: (a, b) => a.stageName.localeCompare(b.stageName),
      },
      {
        key: 'expectedClose',
        dataIndex: 'expectedClose',
        title: 'Expected Close',
        sorter: (a, b) => {
          if (a.expectedClose && b.expectedClose) {
            return new Date(a.expectedClose).getTime() - new Date(b.expectedClose).getTime();
          }
          if (a.expectedClose) {
            return -1;
          }
          if (b.expectedClose) {
            return 1;
          }
          return 0;
        },
        render: item => (item ? format(item, 'MMM dd, yyyy') : ''),
      },
      {
        key: 'amount',
        dataIndex: 'amount',
        title: 'Amount',
        sorter: (a, b) => a.amount - b.amount,
        render: item => (item ? formatNumberAsCurrency(item) : ''),
      },
    ];
  }, [auth]);

  const handleGetPeriod = useCallback(label => {
    if (label === 'Overdue') {
      setSearchParams('stage', 'open');
    } else {
      setSearchParams('stage', null);
    }
  }, []);

  const CSVcolumns = useMemo(
    () => [
      { label: 'client_name', key: 'client' },
      { label: 'opportunity_type', key: 'opportunityType' },
      { label: 'amount', key: 'amount' },
      { label: 'created_date', key: 'createdOn' },
      { label: 'complete_date', key: 'completeDate' },
      { label: 'close_date', key: 'expectedClose' },
      { label: 'store_name', key: 'store' },
      { label: 'associate_name', key: 'createdBy' },
      { label: 'stage_name', key: 'stageName' },
    ],
    [],
  );

  const CSVtable = useMemo(
    () =>
      tableData.map(item => ({
        client: item.client,
        opportunityType: item.opportunityType,
        amount: item.amount,
        createdOn: item.createdOn ? format(item.createdOn, 'yyyy-MM-dd') : '',
        completeDate: item.completeDate ? format(item.completeDate, 'yyyy-MM-dd') : '',
        expectedClose: item.expectedClose ? format(item.expectedClose, 'yyyy-MM-dd') : '',
        store: item.store,
        createdBy: item.createdBy,
        stageName: item.stageName,
      })),
    [tableData],
  );

  return (
    <Report
      title="Sales Pipeline"
      dateFieldOptions={dateFieldOptions}
      dateFieldValue={dateField}
      onDateFieldChange={handleDateFieldChange}
      CSVFilename="sales-pipeline.csv"
      CSVcolumns={CSVcolumns}
      CSVtable={CSVtable}
      options={options}
      stats={stats}
      hasCSVExport
      table={tableData}
      columns={columns}
      filters={filters}
      handleFilterChange={handleFilterChange}
      getPeriodLabel={handleGetPeriod}
      removeFilter={removeFilter}
      onDateRangeChange={handleOnDateRangeChange}
      dataRange={dateRange}
      defaultPeriodValue={getDefaultPeriod}
      stacked
      typeDateOptions={currentTypeDateOptions}
      overdueDate={overdueDate}
      tablesProps={{
        pagination: {
          total,
          page,
          pageSize,
          showSizeChanger: { showSearch: false },
          showTotal: t => `Total ${t} items`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        },
        expandable: {
          expandedRowKeys,
          onExpand: (expanded, record) => handleExpandRow(expanded, record),
          expandedRowRender,
          expandRowByClick: true,
        },
      }}
      graphData={graphData}
      maxTicksLimit={8}
    />
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(SalesOpp);
